import { Pressable, Text, View } from 'react-native'
import { useNavigation, useRoute } from '@react-navigation/native'
import type { NativeStackNavigationProp } from '@react-navigation/native-stack'
import type { RouteProp } from '@react-navigation/native'
import { useTranslation } from 'react-i18next'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Mail, ShieldUser } from 'lucide-react-native'
import { supabase } from '../../../lib/supabase'
import { reportUnexpectedError } from '../../../lib/reportError'
import type { AdminAuthStackParamList } from '../../../navigation/types'
import { Button, InlineAlert, PasswordField, TextField } from '@chillout/ui'
import { AdminAuthLayout } from './AdminAuthLayout'
import { AuthScreenHeader } from './AuthScreenHeader'
import { isActiveAdminAccount } from './adminAccount'
import { clearAdminAuthUrl } from './authUrl'
import { signInSchema } from './authSchemas'

export function AdminSignInScreen() {
  const { t } = useTranslation()
  const navigation = useNavigation<NativeStackNavigationProp<AdminAuthStackParamList>>()
  const route = useRoute<RouteProp<AdminAuthStackParamList, 'SignIn'>>()

  const { control, handleSubmit, setError, clearErrors, getValues, formState: { errors, isSubmitting } } = useForm({
    resolver: zodResolver(signInSchema(t)),
    defaultValues: { email: route.params?.email ?? '', password: '' },
  })

  const onSubmit = handleSubmit(async ({ email, password }) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({ email: email.trim(), password })

      if (error) {
        // Wrong email/password is the routine, expected outcome here — not a bug.
        setError('root', { message: t('adminAuth.signInError') })
        return
      }

      const isActiveAdmin = await isActiveAdminAccount(data.user.id)
      if (!isActiveAdmin) {
        await supabase.auth.signOut()
        setError('root', { message: t('adminAuth.signInError') })
        return
      }

      await supabase.rpc('log_admin_auth_event', { p_event_type: 'admin_signed_in' })
      clearAdminAuthUrl()
      navigation.navigate('AdminHome')
    } catch (err) {
      reportUnexpectedError(err)
      setError('root', { message: t('adminAuth.signInError') })
    }
  })

  return (
    <AdminAuthLayout>
      <AuthScreenHeader
        Icon={ShieldUser}
        iconAccessibilityLabel={t('adminAuth.signInIconAccessibilityLabel')}
        title={t('adminAuth.signInPlaceholder')}
        subtitle={t('adminAuth.signInSubtitle')}
      />
      <View className="mt-8 w-full sm:mt-10">
        <Controller
          control={control}
          name="email"
          render={({ field, fieldState }) => (
            <TextField
              label={t('adminAuth.emailLabel')}
              placeholder={t('adminAuth.emailPlaceholder')}
              keyboardType="email-address"
              autoCapitalize="none"
              autoComplete="email"
              textContentType="emailAddress"
              prefix={Mail}
              value={field.value}
              onChangeText={(v) => { field.onChange(v); clearErrors('root') }}
              onBlur={field.onBlur}
              returnKeyType="next"
              onSubmitEditing={onSubmit}
              error={fieldState.error?.message}
            />
          )}
        />
        <View className="mt-6">
          <Controller
            control={control}
            name="password"
            render={({ field, fieldState }) => (
              <PasswordField
                label={t('adminAuth.passwordLabel')}
                placeholder={t('adminAuth.passwordPlaceholder')}
                showPasswordAccessibilityLabel={t('adminAuth.showPasswordAccessibilityLabel')}
                hidePasswordAccessibilityLabel={t('adminAuth.hidePasswordAccessibilityLabel')}
                value={field.value}
                onChangeText={(v) => { field.onChange(v); clearErrors('root') }}
                onBlur={field.onBlur}
                returnKeyType="go"
                onSubmitEditing={onSubmit}
                error={fieldState.error?.message}
              />
            )}
          />
        </View>
        <View className="mt-3 items-start">
          <Pressable
            onPress={() => navigation.navigate('ForgotPassword', { email: getValues('email').trim() })}
            accessibilityRole="link"
            accessibilityLabel={t('adminAuth.forgotPassword')}
          >
            <Text className="text-base font-medium text-accent">
              {t('adminAuth.forgotPassword')}
            </Text>
          </Pressable>
        </View>
        {errors.root ? (
          <View className="mt-6">
            <InlineAlert message={errors.root.message ?? ''} />
          </View>
        ) : null}
        <Button
          label={t('adminAuth.signInButton')}
          loadingLabel={t('adminAuth.signingInButton')}
          loading={isSubmitting}
          onPress={onSubmit}
          className={errors.root ? 'mt-6' : 'mt-10'}
        />
      </View>
    </AdminAuthLayout>
  )
}